import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import askAiOff from '../../../../assets/alison-run/ask-ai-off.svg';
import closeX from '../../../../assets/alison-run/close-x.svg';
import { useAlisonRun } from '../AlisonRunProvider.js';
import {
  ALISON_DIVIDER_SOFT,
  ALISON_DRAWER_CTA_GRADIENT,
  ALISON_TEXT_SUBTLE,
  ALISON_TEXT_TITLE,
} from './alisonRunColors.js';

export function DrawerHeader({
  subtitle,
  height,
  running,
}: {
  subtitle: string;
  height: number;
  running: boolean;
}): ReactNode {
  const { copy, closeDrawer } = useAlisonRun();
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: '12px',
        height,
        flexShrink: 0,
        p: '24px',
        bgcolor: 'background.paper',
      }}
    >
      <Box component="img" src={askAiOff} alt="" sx={{ width: 32, height: 32, flexShrink: 0 }} />
      <Stack sx={{ flex: 1, minWidth: 0, gap: '2px' }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 'normal',
            color: running ? ALISON_TEXT_TITLE : 'background.icon',
          }}
        >
          {copy.title}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 'normal',
            color: running ? ALISON_TEXT_SUBTLE : 'text.muted',
          }}
        >
          {subtitle}
        </Typography>
      </Stack>
      <ButtonBase
        aria-label={copy.close}
        onClick={closeDrawer}
        sx={{ width: 16, height: 16, flexShrink: 0 }}
      >
        <Box component="img" src={closeX} alt="" sx={{ width: 16, height: 16 }} />
      </ButtonBase>
    </Stack>
  );
}

export function GradientButton({
  onClick,
  disabled = false,
  busy = false,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <Button
      variant="gradient"
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy}
      sx={{
        minWidth: 0,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        gap: '8px',
        px: '12px',
        py: '8px',
        borderRadius: '9px',
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 'normal',
        boxShadow: 'none',
        background: ALISON_DRAWER_CTA_GRADIENT,
        '&:hover': { background: ALISON_DRAWER_CTA_GRADIENT, filter: 'brightness(0.95)' },
      }}
    >
      {children}
    </Button>
  );
}

export function DrawerFooter({ note, children }: { note: string; children: ReactNode }): ReactNode {
  return (
    <Stack
      sx={{
        height: 78,
        flexShrink: 0,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ height: '1px', flexShrink: 0, bgcolor: ALISON_DIVIDER_SOFT }} />
      <Stack
        direction="row"
        sx={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: '24px',
          px: '20px',
          py: '16px',
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 11.5,
            fontWeight: 400,
            lineHeight: 'normal',
            color: ALISON_TEXT_SUBTLE,
          }}
        >
          {note}
        </Typography>
        <Stack direction="row" sx={{ gap: '10px', alignItems: 'center', flexShrink: 0 }}>
          {children}
        </Stack>
      </Stack>
    </Stack>
  );
}
